import React from "react";
import { createPortal } from "react-dom";
import { useMap } from "react-leaflet";
import type { PhotoCoverageNoticeItem } from "@/features/map/utils/photoCoverage";

interface Props {
  items: PhotoCoverageNoticeItem[];
}

const messageFor = (item: PhotoCoverageNoticeItem): string | null => {
  switch (item.status) {
    case "none":
      return `${item.label}：この範囲に写真はありません`;
    case "partial":
      return `${item.label}：一部の範囲のみ写真があります`;
    case "out-of-zoom":
      return `${item.label}：ズームレベル ${item.minZoom} 以上で表示されます`;
    default:
      return null;
  }
};

/**
 * 写真レイヤーの「この範囲にデータがない」ことを地図上部中央に小さく知らせる。
 *
 * 写真レイヤーは年代によって撮影範囲が狭く、ON にしても何も変わらないことが多い。
 * タイルの読み込み結果（404）から判定した結果を、地図操作を妨げない帯で表示する。
 * データがそろっているレイヤー・判定中のレイヤーは表示しない。
 */
export const PhotoCoverageNotice: React.FC<Props> = ({ items }) => {
  const map = useMap();
  const messages = items
    .map(messageFor)
    .filter((message): message is string => message !== null);

  if (messages.length === 0) {
    return null;
  }

  return createPortal(
    <div
      role="status"
      style={{
        // 左上のズーム等のコントロール列の右隣に置く。中央寄せにするとレイヤー選択パネルと重なるため
        position: "absolute",
        top: 10,
        left: 56,
        zIndex: 1000,
        pointerEvents: "none",
        maxWidth: "min(calc(100% - 120px), 420px)",
        padding: "6px 10px",
        borderRadius: 6,
        background: "rgba(31, 45, 61, 0.82)",
        color: "#fff",
        fontSize: 12,
        lineHeight: 1.5,
        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
      }}
    >
      {messages.map((message) => (
        <div key={message}>{message}</div>
      ))}
    </div>,
    map.getContainer()
  );
};
